import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { store } from 'react-notifications-component'
import {
  getPaymentCharges,
  getPaymentCharge,
  createPaymentCharge,
  updatePaymentCharge,
  toggleFeeConfigState,
  getPaymentProviders,
  getSites,
} from '@services/queries'
import {
  PAYMENT_CHARGE_CREATE_SUCCESS_MESSAGE,
  PAYMENT_CHARGE_CREATE_FAILURE_MESSAGE,
  PAYMENT_CHARGE_UPDATE_SUCCESS_MESSAGE,
  PAYMENT_CHARGE_UPDATE_FAILURE_MESSAGE,
} from '@constants'

export function usePaymentCharges(filters = {}) {
  const chargesQuery = useQuery(
    [
      'payment-charges',
      filters.brand_id,
      filters.payment_method_name,
      filters.transaction_type,
      filters.is_active,
    ],
    () => getPaymentCharges(filters),
  )

  const sitesQuery = useQuery('sites', getSites)

  const sitesById = useMemo(
    () =>
      (sitesQuery.data || []).reduce((acc, site) => {
        acc[site.id] = site.name
        return acc
      }, {}),
    [sitesQuery?.data],
  )

  const data = useMemo(
    () =>
      chargesQuery.data?.map((charge) => ({
        ...charge,
        brand_name: sitesById[charge.brand_id] ?? charge.brand_id,
      })),
    [chargesQuery.data, sitesById],
  )

  return {
    ...chargesQuery,
    data,
  }
}

export function usePaymentCharge(id) {
  return useQuery(['payment-charge', id], () => getPaymentCharge(id), {
    enabled: !!id,
  })
}

export function useCreatePaymentChargeMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  return useMutation((payload) => createPaymentCharge(payload), {
    onSuccess(data) {
      queryClient.invalidateQueries(['payment-charges'])
      store.addNotification(PAYMENT_CHARGE_CREATE_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data)
    },
    onError(err) {
      store.addNotification(PAYMENT_CHARGE_CREATE_FAILURE_MESSAGE)
      if (onError) onError(err)
    },
  })
}

export function useUpdatePaymentChargeMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  return useMutation(({ id, payload }) => updatePaymentCharge(id, payload), {
    onSuccess(data, variables) {
      queryClient.invalidateQueries(['payment-charges'])
      queryClient.invalidateQueries(['payment-charge', variables.id])
      store.addNotification(PAYMENT_CHARGE_UPDATE_SUCCESS_MESSAGE)
      if (onSuccess) onSuccess(data)
    },
    onError(err) {
      store.addNotification(PAYMENT_CHARGE_UPDATE_FAILURE_MESSAGE)
      if (onError) onError(err)
    },
  })
}

export function useToggleFeeConfigStateMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  return useMutation(({ id, isActive }) => toggleFeeConfigState(id, isActive), {
    onSuccess(data, variables) {
      queryClient.invalidateQueries(['payment-charges'])
      queryClient.invalidateQueries(['payment-charge', variables.id])
      if (onSuccess) onSuccess(data)
    },
    onError(err) {
      if (onError) onError(err)
    },
  })
}

export function usePaymentProviders() {
  return useQuery(['payment-providers'], getPaymentProviders)
}
